import { createContext, useContext, useState, useEffect, ReactNode } from "react";
import { Language, translations } from "./translations";

type TranslatedString = { no: string; en: string; de: string };

interface LanguageContextType {
  language: Language;
  setLanguage: (lang: Language) => void;
  t: (field: TranslatedString) => string;
}

const LanguageContext = createContext<LanguageContextType | null>(null);

const STORAGE_KEY = "envir-language";
const isLanguage = (v: unknown): v is Language => v === "no" || v === "en" || v === "de";

export const LanguageProvider = ({ children }: { children: ReactNode }) => {
  // Always start with "no" so the first render matches the prerendered HTML
  // (avoids React hydration error #423); apply the stored language afterwards.
  const [language, setLanguageState] = useState<Language>("no");
  useEffect(() => {
    try {
      const stored = window.localStorage.getItem(STORAGE_KEY);
      if (isLanguage(stored) && stored !== "no") setLanguageState(stored);
    } catch {}
  }, []);

  const setLanguage = (lang: Language) => {
    setLanguageState(lang);
    try {
      window.localStorage.setItem(STORAGE_KEY, lang);
    } catch {}
  };

  useEffect(() => {
    document.documentElement.lang = language;
  }, [language]);

  const t = (field: TranslatedString) => field[language] || field.no;

  return (
    <LanguageContext.Provider value={{ language, setLanguage, t }}>
      {children}
    </LanguageContext.Provider>
  );
};

export const useLanguage = () => {
  const ctx = useContext(LanguageContext);
  if (!ctx) throw new Error("useLanguage must be used within LanguageProvider");
  return ctx;
};

export { translations };
